import { Caption } from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import * as React from 'react';
import styled, { css } from 'styled-components';

import BidConsoleOpener from '@/components/BidConsoleOpener/BidConsoleOpener';
import CatalogDate from '@/components/CatalogDate/CatalogDate';
import { useAppSelector } from '@/redux/hooks';
import { getLiveCatalogCurrentItemIndex } from '@/redux/modules/console';
import {
    getItemSummaryBuyNowStatus,
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIndex,
    getItemSummaryIsAvailable,
    getItemSummaryIsCatalogOnly,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummarySaleStartTs,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { AuctionStatus } from '@/types/LiveAuctionState';
import { shouldShowLive } from '@/utils/live';
import noop from '@/utils/noop';

export type LiveButtonProps = {
    allowBidding?: boolean;
    isCatalogPage?: boolean;
    isLiveButtonActive?: (setBoolean: boolean) => void;
    isMobile?: boolean;
    itemId: number;
    showDate?: boolean;
};

const ItemCardLiveButton = ({
    allowBidding,
    isCatalogPage,
    isLiveButtonActive = noop,
    itemId,
    showDate,
}: LiveButtonProps) => {
    const buyNowStatus = useAppSelector((state) => getItemSummaryBuyNowStatus(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const isAvailable = useAppSelector((state) => getItemSummaryIsAvailable(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryIsCatalogOnly(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummarySaleStartTs(state, itemId));

    const currentItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));

    const showLiveButton = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
    const buyNowAvailable = buyNowStatus === 1 && catalogStatus === AuctionStatus.Done;
    const lotsAway = index - currentItemIndex;
    const isComingUp = isLotOpen || (currentItemIndex <= index && isAvailable && !isPassed && !isSold);

    if (buyNowAvailable) {
        return null;
    }

    if (showLiveButton && isComingUp) {
        return (
            <StyledWrapper>
                <ItemLiveSpan $isCatalogPageItemCard={isCatalogPage}>
                    <BidConsoleOpener catalogId={catalogId}>
                        <ItemLiveLinkSpan
                            onMouseOut={() => isLiveButtonActive(false)}
                            onMouseOver={() => isLiveButtonActive(true)}
                        >
                            {lotsAway > 0 && allowBidding ? (
                                lotsAway === 1 && allowBidding ? (
                                    <FormattedMessage id="itemBidding.lotAway_short" />
                                ) : (
                                    <FormattedMessage
                                        id="itemBidding.lotsAway_short"
                                        values={{ lotsAway }}
                                    />
                                )
                            ) : (
                                <FormattedMessage id="bid_live" />
                            )}
                        </ItemLiveLinkSpan>
                    </BidConsoleOpener>
                </ItemLiveSpan>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper>
            <CatalogDate
                date={saleStartTs}
                showDate={showDate}
            />
        </StyledWrapper>
    );
};

export default ItemCardLiveButton;

const StyledWrapper = styled(Caption)<{ $isLotOpen?: boolean }>`
    flex: 0 1 auto;
    display: block;
    ${({ $isLotOpen }) => {
        if ($isLotOpen) {
            return css`
                padding: 8px 0 4px 0;
            `;
        }
    }}
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        font-size: 14px;
        line-height: 21px;
    }
`;

interface ItemLiveProps {
    $isCatalogPageItemCard?: boolean;
}

const ItemLiveSpan = styled.span<ItemLiveProps>`
    display: inline-block;
    ${({ $isCatalogPageItemCard }) =>
        $isCatalogPageItemCard &&
        css`
            padding: 8px 0 0 0;
        `}
`;

const ItemLiveLinkSpan = styled.span`
    color: ${({ theme }) => theme.colors.red100};
    display: inline-block;
    position: relative;
    padding-right: 10px;
    cursor: pointer;

    &:hover {
        color: ${({ theme }) => theme.colors.red200};
        text-decoration: underline;
        &::after {
            border-left: 4px solid ${({ theme }) => theme.colors.red200};
        }
    }

    &::after {
        position: absolute;
        content: '';
        width: 0;
        height: 0;
        right: 0;
        top: 5px;
        clear: both;
        border-top: 4px solid transparent;
        border-bottom: 4px solid transparent;
        border-left: 4px solid ${({ theme }) => theme.colors.red100};

        @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
            top: 7px;
        }
    }
`;
